import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Edit2, Plus, RefreshCw, Check, ChevronRight, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { mealPlanAPI, profileAPI } from '../utils/api';
import { toast } from 'sonner';
import { getTodayString, formatDateString, normalizeDate } from '../utils/dateUtils';
import { hasRealNutritionTargets } from '../utils/nutritionTargets';
import NutritionTargetsSetupPrompt from '../components/NutritionTargetsSetupPrompt';

// Weekly Plan Review - View and edit generated meal plan
export default function WeeklyPlanReview() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const startDate = searchParams.get('start') || getTodayString();
  
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [weekPlan, setWeekPlan] = useState({});
  const [editingDay, setEditingDay] = useState(null);
  
  // Get week dates
  const getWeekDates = useCallback(() => {
    const dates = [];
    // normalizeDate parses "YYYY-MM-DD" as local midday — raw `new Date(str)`
    // shifts the whole week back one day in negative-UTC-offset timezones
    // (see AGENTS.md pre-launch audit, finding DATE-1).
    const start = normalizeDate(startDate);
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      dates.push(formatDateString(d));
    }
    return dates;
  }, [startDate]);
  
  // Load the week's meal plan
  useEffect(() => {
    const loadPlan = async () => {
      setLoading(true);
      try {
        const profileRes = await profileAPI.getProfile();
        setProfile(profileRes.data);
        
        const weekDates = getWeekDates();
        const planData = {};
        
        for (const date of weekDates) {
          const res = await mealPlanAPI.getDailyPlan(date);
          planData[date] = res.data || [];
        }
        
        setWeekPlan(planData);
      } catch (err) {
        console.error('Failed to load plan:', err);
        toast.error('Failed to load meal plan');
      } finally {
        setLoading(false);
      }
    };
    
    loadPlan();
  }, [getWeekDates]);
  
  // Calculate daily macros for a date
  const getDayMacros = (date) => {
    const meals = weekPlan[date] || [];
    let calories = 0, protein = 0, carbs = 0, fat = 0;
    
    meals.forEach(meal => {
      const m = meal.macros || {};
      calories += m.calories || meal.calories || 0;
      protein += m.protein || meal.protein || 0;
      carbs += m.carbs || meal.carbs || 0;
      fat += m.fat || meal.fat || 0;
    });
    
    return { calories, protein, carbs, fat };
  };
  
  // Get daily targets. Returns null when the user has no real, personalized
  // targets yet — callers must not substitute a generic fallback and present
  // it as the user's own (see AGENTS.md pre-launch audit, finding ONB-2).
  const getDailyTargets = () => {
    if (!hasRealNutritionTargets(profile)) return null;
    return {
      calories: profile.target_calories ?? profile.calories,
      protein: profile.target_protein_g ?? profile.protein,
      carbs: profile.target_carbs_g ?? profile.carbs,
      fat: profile.target_fat_g ?? profile.fat
    };
  };
  
  // Check if day meets macro goals. No real targets yet -> unknown, not "on target".
  const isDayOnTarget = (date) => {
    const targets = getDailyTargets();
    if (!targets) return false;
    const macros = getDayMacros(date);

    // Within 10% of target is considered on target
    const calDiff = Math.abs(macros.calories - targets.calories) / targets.calories;
    return calDiff <= 0.1;
  };

  // Calculate macro gap for a day
  const getDayGap = (date) => {
    const targets = getDailyTargets();
    if (!targets) return { calories: 0, protein: 0, carbs: 0, fat: 0 };
    const macros = getDayMacros(date);

    return {
      calories: targets.calories - macros.calories,
      protein: targets.protein - macros.protein,
      carbs: targets.carbs - macros.carbs,
      fat: targets.fat - macros.fat
    };
  };
  
  // Navigate to add more meals for a specific day
  const handleAddMeals = (date) => {
    navigate(`/meals?guided=true&date=${date}&return=/plan/weekly-review?start=${startDate}`);
  };
  
  // Handle close the gap
  const handleCloseGap = (date) => {
    const gap = getDayGap(date);
    // Navigate to browse with macro filters to find meals that fit the gap
    navigate(`/meals?guided=true&date=${date}&maxCal=${gap.calories}&return=/plan/weekly-review?start=${startDate}`);
  };
  
  // Delete a meal from the plan
  const handleRemoveMeal = async (planId, date) => {
    try {
      await mealPlanAPI.deleteMealPlan(planId);
      setWeekPlan(prev => ({
        ...prev,
        [date]: (prev[date] || []).filter(m => m.plan_id !== planId)
      }));
      toast.success('Meal removed');
    } catch (err) {
      console.error('Failed to remove meal:', err);
      toast.error('Failed to remove meal');
    }
  };
  
  const weekDates = getWeekDates();
  const targets = getDailyTargets();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-accent border-t-transparent rounded-full" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border/50 px-4 py-3 header-safe-top">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate('/plan')} className="p-2 -ml-2">
            <ArrowLeft size={24} />
          </button>
          <h1 className="font-semibold">Your Week Plan</h1>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/plan/weekly?start=${startDate}`)}
          >
            <Edit2 size={16} className="mr-1" />
            Edit
          </Button>
        </div>
        
        <div className="mt-2 text-center text-sm text-muted-foreground">
          Week of {normalizeDate(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
        </div>
      </header>
      
      {/* Days List */}
      <div className="px-4 py-4 space-y-4">
        {!targets && <NutritionTargetsSetupPrompt />}
        {weekDates.map((date, idx) => {
          const dayMacros = getDayMacros(date);
          const dayMeals = weekPlan[date] || [];
          const gap = getDayGap(date);
          const onTarget = isDayOnTarget(date);
          const hasGap = gap.calories > 200;
          
          return (
            <motion.div
              key={date}
              initial={{ opacity: 0, y: 0 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              className="bg-card rounded-xl border border-border/50 overflow-hidden"
            >
              {/* Day Header */}
              <div className={`p-3 border-b border-border/30 ${onTarget ? 'bg-green-500/10' : hasGap ? 'bg-amber-500/10' : ''}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold">
                      {normalizeDate(date).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                    </h3>
                    <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                      <span>{Math.round(dayMacros.calories)} / {targets ? targets.calories : '--'} cal</span>
                      <span className="text-red-500">{Math.round(dayMacros.protein)}g P</span>
                      <span className="text-blue-500">{Math.round(dayMacros.carbs)}g C</span>
                      <span className="text-yellow-500">{Math.round(dayMacros.fat)}g F</span>
                    </div>
                  </div>
                  
                  {onTarget ? (
                    <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                      <Check size={16} className="text-white" />
                    </div>
                  ) : hasGap ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleCloseGap(date)}
                      className="text-amber-500 border-amber-500"
                    >
                      <AlertTriangle size={14} className="mr-1" />
                      Close Gap
                    </Button>
                  ) : null}
                </div>
              </div>
              
              {/* Meals */}
              <div className="p-3 space-y-2">
                {dayMeals.length === 0 ? (
                  <div className="text-center py-4 text-muted-foreground text-sm">
                    No meals planned
                  </div>
                ) : (
                  dayMeals.map(meal => (
                    <div 
                      key={meal.plan_id}
                      className="flex items-center justify-between p-2 bg-secondary/30 rounded-lg"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-muted-foreground">{meal.meal_type}</div>
                        <div className="font-medium text-sm truncate">{meal.meal_name}</div>
                        <div className="text-xs text-muted-foreground">
                          {meal.macros?.calories || meal.calories || 0} cal • 
                          {meal.macros?.protein || meal.protein || 0}g P
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveMeal(meal.plan_id, date)}
                        className="p-2 text-red-500 hover:bg-red-500/10 rounded"
                      >
                        ×
                      </button>
                    </div>
                  ))
                )}
                
                {/* Add more button */}
                <button
                  onClick={() => handleAddMeals(date)}
                  className="w-full p-2 border border-dashed border-border rounded-lg text-sm text-muted-foreground hover:border-accent hover:text-accent transition-colors flex items-center justify-center gap-1"
                >
                  <Plus size={14} />
                  Add Meal
                </button>
              </div>
            </motion.div>
          );
        })}
      </div>
      
      {/* Bottom Actions */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-background/80 backdrop-blur-lg border-t border-border/50">
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => navigate(`/plan/weekly?start=${startDate}`)}
          >
            <RefreshCw size={16} className="mr-2" />
            Add More Meals
          </Button>
          <Button
            className="flex-1 bg-accent"
            onClick={() => {
              toast.success('Meal plan saved!');
              navigate('/plan');
            }}
          >
            <Check size={16} className="mr-2" />
            Done
          </Button>
        </div>
      </div>
    </div>
  );
}
